import { LoaderFunctionArgs } from 'react-router'
import { AxiosError } from 'axios'
import { queryClient } from '@/api/client'
import { unitSettingsQueryOptions } from '../../cogs/api'
import { LoaderResponse } from '@/types'
import { getInventorySetUpQueryOptions } from '../api/inventory'
import { tryCatch } from '@/lib/utils/try-catch'
import { PayableConfiguration, useTwcStore } from '../store'
import { ReceivableSetup, InventorySetup } from '../types'
import { getReceivableSetupQueryOptions } from '../api/receivable'
import { getPayableSetUpQueryOptions } from '../api/payable'
import { parsePayableConfiguration } from '../utils'

type TWCConfig = {
  inventory: InventorySetup | null
  receivable: ReceivableSetup | null
  payable: PayableConfiguration | null
}

export const twcLoader = async ({ params }: LoaderFunctionArgs) => {
  const { id: financialModelId } = params
  if (!financialModelId) {
    return {
      cogsGranularity: null
    }
  }
  const twcConfig: TWCConfig = {
    inventory: null,
    receivable: null,
    payable: null
  }
  const cogsSettings = await queryClient.ensureQueryData(unitSettingsQueryOptions(financialModelId))
  const { setInventoryApproach, setInventoryGranularity, setReceivableApproach, setReceivableGranularity, setPayableConfiguration } =
    useTwcStore.getState()
  const [receivableSetup, receivableError] = await tryCatch(queryClient.ensureQueryData(getReceivableSetupQueryOptions(financialModelId)))
  if (receivableError) {
    if (receivableError instanceof AxiosError && receivableError.status === 404) {
      twcConfig.receivable = null
    } else {
      throw receivableError
    }
  } else {
    twcConfig.receivable = receivableSetup
    setReceivableApproach(receivableSetup?.modeling_approach ?? null)
    setReceivableGranularity(receivableSetup?.level_of_granularity ?? null)
  }

  const [payableSetup, payableError] = await tryCatch(queryClient.ensureQueryData(getPayableSetUpQueryOptions(financialModelId)))
  if (payableError) {
    if (payableError instanceof AxiosError && payableError.status === 404) {
      twcConfig.payable = {
        total_costs: {
          id: `total_costs`,
          level_of_granularity: `aggregate_costs`,
          modeling_approach: null,
          enabled: true
        },
        direct_costs: {
          id: `direct_costs`,
          level_of_granularity: null,
          modeling_approach: null,
          enabled: false
        },
        operating_costs: {
          id: `operating_costs`,
          level_of_granularity: null,
          modeling_approach: null,
          enabled: false
        }
      }
    } else {
      throw payableError
    }
  } else {
    twcConfig.payable = parsePayableConfiguration(payableSetup)
    setPayableConfiguration(twcConfig.payable)
  }

  const [inventorySetup, inventoryError] = await tryCatch(queryClient.ensureQueryData(getInventorySetUpQueryOptions(financialModelId)))

  if (inventoryError) {
    if (inventoryError instanceof AxiosError && inventoryError.status === 404) {
      twcConfig.inventory = null
    } else {
      throw inventoryError
    }
  } else {
    twcConfig.inventory = inventorySetup
    setInventoryApproach(inventorySetup?.modeling_approach ?? null)
    setInventoryGranularity(inventorySetup?.level_of_granularity ?? null)
  }

  const cogsGranularity = cogsSettings.level_of_granularity
  return {
    cogsGranularity,
    ...twcConfig
  }
}

export type TwcLoaderResponse = LoaderResponse<typeof twcLoader>
